
import React from "react";
import { Link, useMatch, useResolvedPath } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

interface SidebarLinkProps {
  to: string;
  icon: LucideIcon;
  label: string;
  collapsed: boolean;
  currentPathname: string;
  onLinkClick?: () => void;
}

export const SidebarLink: React.FC<SidebarLinkProps> = ({
  to,
  icon: Icon,
  label,
  collapsed,
  currentPathname,
  onLinkClick,
}) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: to === "/" });

  // Improved active state detection
  const isActive = match || (to !== "/" && currentPathname.startsWith(to) && !currentPathname.startsWith(to + '/new') && !currentPathname.includes('/jobs/') && !currentPathname.includes('/candidates/') && !currentPathname.includes('/clients/') && !currentPathname.includes('/leads/'));

  const linkContent = (
    <>
      <Icon className={cn(
        "h-5 w-5 shrink-0 transition-colors duration-150",
        isActive 
          ? "text-sidebar-primary" 
          : "text-sidebar-foreground/70 group-hover:text-sidebar-primary"
      )} />
      <span
        className={cn(
          "text-sm font-medium transition-all duration-300",
          isActive 
            ? "text-sidebar-primary" 
            : "text-sidebar-foreground group-hover:text-sidebar-primary",
          collapsed ? "opacity-0 w-0" : "opacity-100 w-auto"
        )}
      >
        {label}
      </span>
    </>
  );

  const handleClick = () => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={to}
              onClick={handleClick}
              className={cn(
                "group flex items-center gap-x-3 rounded-md p-2 transition-colors duration-150",
                isActive 
                  ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                  : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
            >
              {linkContent}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-popover text-popover-foreground border border-border">
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Link
      to={to}
      onClick={handleClick}
      className={cn(
        "group flex items-center gap-x-3 rounded-md p-2 transition-colors duration-150",
        isActive 
          ? "bg-sidebar-accent text-sidebar-accent-foreground" 
          : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      {linkContent}
    </Link>
  );
};
